import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { MatchmakerLayout } from "@/components/matchmaker/MatchmakerLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Step1BasicInfo } from "@/components/profile-creation/Step1BasicInfo";
import { Step2ReligiousProfile } from "@/components/profile-creation/Step2ReligiousProfile";
import { Step3EducationCareer } from "@/components/profile-creation/Step3EducationCareer";
import { Step4LifestylePersonality } from "@/components/profile-creation/Step4LifestylePersonality";
import { Step5Preferences } from "@/components/profile-creation/Step5Preferences";
import { QuickImport } from "@/components/profile-creation/QuickImport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Info, UserPlus } from "lucide-react";
import { ProfileFormData } from "@/pages/ProfileCreation";
import {
  formatPhoneInput,
  isPlausiblePhone,
  normalizePhone,
  PHONE_ERROR_MESSAGE,
} from "@/lib/phone";

const STEPS = [
  { title: "Basic Info", description: "Basic information about the user" },
  { title: "Religious Profile", description: "Faith and practice details" },
  { title: "Education & Career", description: "Professional background" },
  { title: "Lifestyle & Personality", description: "Interests and values" },
  { title: "What They're Looking For", description: "Partner preferences" },
  { title: "Review & Invite", description: "Review and send invitation" },
];

export default function MatchmakerCreateProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Matchmaker-specific fields
  const [inviteEmail, setInviteEmail] = useState("");
  const [consentGiven, setConsentGiven] = useState(false);

  const [formData, setFormData] = useState<ProfileFormData>({
    first_name: "",
    age: 25,
    gender: "male",
    location: "",
    education_level: "",
    occupation: "",
    ethnicity: "Prefer not to say",
    citizenship_status: "US Citizen",
    marital_status: "Never Married",
    has_children: false,
    willing_to_relocate: false,
    religiosity_level: "moderate",
    prayer_frequency: "working on it",
    importance_of_islam: 5,
    sect: "Sunni",
    masjid_attendance: "Jumuah sometimes",
    quran_knowledge: "Basic",
    islamic_knowledge_level: "Basic",
    employment_status: "Employed full-time",
    financial_status: 3,
    personality_type: 5,
    personality_description: "",
    dietary_preferences: [],
    smoking: "Never",
    bio: "",
    seeking_age_min: 22,
    seeking_age_max: 35,
    seeking_ethnicity: [],
    seeking_sect: [],
    seeking_marital_status: [],
    seeking_children_ok: true,
    seeking_convert_ok: true,
    seeking_divorced_ok: true,
    seeking_religiosity: [],
    seeking_education: [],
    seeking_location_preference: "",
    seeking_description: "",
    email: "",
    phone: "",
    whatsapp: "",
    // Left blank on purpose: a matchmaker filling in someone else's contact
    // details shouldn't guess how that person prefers to be reached. It stays
    // NULL until the member picks it themselves when they claim the profile.
    preferred_contact_method: "",
  });

  const handleQuickImport = (extractedData: Partial<ProfileFormData>) => {
    setFormData(prev => ({ ...prev, ...extractedData }));
    toast.success("Data imported successfully");
  };

  const handleStepComplete = async (stepData: Partial<ProfileFormData>) => {
    const updatedData = { ...formData, ...stepData };
    setFormData(updatedData);

    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinalSubmit = async () => {
    if (!user) {
      toast.error("You must be logged in as a matchmaker");
      return;
    }

    // Debug: Check session
    const { data: { session: currentSession } } = await supabase.auth.getSession();

    if (!currentSession) {
      toast.error("Session expired. Please log in again.");
      return;
    }

    // Validation
    if (!inviteEmail || !inviteEmail.includes("@")) {
      toast.error("Please provide a valid email address for the invitation");
      return;
    }

    if (!consentGiven) {
      toast.error("You must confirm that you have consent to create this profile");
      return;
    }

    if (!formData.first_name || !formData.age || !formData.gender) {
      toast.error("Please fill in all required basic information");
      return;
    }

    // Phone/WhatsApp are optional here — only judge what was actually entered.
    if (formData.phone && !isPlausiblePhone(formData.phone)) {
      toast.error(`Phone: ${PHONE_ERROR_MESSAGE}`);
      return;
    }
    if (formData.whatsapp && !isPlausiblePhone(formData.whatsapp)) {
      toast.error(`WhatsApp: ${PHONE_ERROR_MESSAGE}`);
      return;
    }

    setSaving(true);

    try {
      // Refresh the session to ensure we have a valid, non-expired auth token
      const { data: { session }, error: sessionError } = await supabase.auth.refreshSession();

      if (sessionError || !session) {
        console.error("matchmaker.session_refresh_failed");
        toast.error("Your session has expired. Please log in again.");
        setSaving(false);
        return;
      }

      // Call edge function to create stub auth user, profile, and send invitation
      // This needs to be done server-side because auth.admin methods aren't available on client
      const { data: result, error: functionError } = await supabase.functions.invoke('create-matchmaker-profile', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: {
          invite_email: inviteEmail,
          matchmaker_id: user.id,
          consent_given: consentGiven,
          profile_data: {
            // Basic Info
            first_name: formData.first_name,
            age: formData.age,
            gender: formData.gender,
            location: formData.location,
            education_level: formData.education_level,
            occupation: formData.occupation,
            height_feet: formData.height_feet,
            height_inches: formData.height_inches,
            ethnicity: formData.ethnicity,
            citizenship_status: formData.citizenship_status,
            marital_status: formData.marital_status,
            has_children: formData.has_children,
            children_count: formData.children_count,
            willing_to_relocate: formData.willing_to_relocate,
            profile_picture_url: formData.profile_picture_url,
            profile_pictures: formData.profile_pictures,
            // Religious Profile
            religiosity_level: formData.religiosity_level,
            prayer_frequency: formData.prayer_frequency,
            wears_hijab: formData.wears_hijab,
            importance_of_islam: formData.importance_of_islam,
            sect: formData.sect,
            masjid_attendance: formData.masjid_attendance,
            wears_hijab_level: formData.wears_hijab_level,
            has_beard: formData.has_beard,
            quran_knowledge: formData.quran_knowledge,
            islamic_knowledge_level: formData.islamic_knowledge_level,
            // Education & Career
            field_of_study: formData.field_of_study,
            employment_status: formData.employment_status,
            financial_status: formData.financial_status,
            career_description: formData.career_description,
            // Lifestyle & Personality
            personality_type: formData.personality_type,
            personality_description: formData.personality_description,
            dietary_preferences: formData.dietary_preferences,
            smoking: formData.smoking,
            exercise_routine: formData.exercise_routine,
            bio: formData.bio,
            // Seeking Preferences
            seeking_age_min: formData.seeking_age_min,
            seeking_age_max: formData.seeking_age_max,
            seeking_height_min_feet: formData.seeking_height_min_feet,
            seeking_height_min_inches: formData.seeking_height_min_inches,
            seeking_height_max_feet: formData.seeking_height_max_feet,
            seeking_height_max_inches: formData.seeking_height_max_inches,
            seeking_ethnicity: formData.seeking_ethnicity,
            seeking_sect: formData.seeking_sect,
            seeking_religiosity: formData.seeking_religiosity,
            seeking_masjid_attendance_min: formData.seeking_masjid_attendance_min,
            seeking_location_preference: formData.seeking_location_preference,
            seeking_hijab_preference: formData.seeking_hijab_preference,
            seeking_beard_preference: formData.seeking_beard_preference,
            seeking_citizenship: formData.seeking_citizenship,
            seeking_marital_status: formData.seeking_marital_status,
            seeking_children_ok: formData.seeking_children_ok,
            seeking_convert_ok: formData.seeking_convert_ok,
            seeking_divorced_ok: formData.seeking_divorced_ok,
            seeking_personality_min: formData.seeking_personality_min,
            seeking_personality_max: formData.seeking_personality_max,
            seeking_financial_min: formData.seeking_financial_min,
            seeking_education: formData.seeking_education,
            seeking_description: formData.seeking_description,
          },
          contact_data: {
            email: inviteEmail,
            // Stored normalized (E.164 where parseable) — see @/lib/phone.
            phone: normalizePhone(formData.phone),
            whatsapp: normalizePhone(formData.whatsapp),
            // NULL until the member chooses it on claim (see formData init).
            preferred_contact_method: null,
          },
        }
      });

      if (functionError) {
        console.error("matchmaker.create_profile_edge_fn_failed");

        // The Supabase client doesn't expose response body on error
        // Check for already registered errors
        const errorMsg = functionError.message || "";
        if (errorMsg.includes('already') || errorMsg.includes('registered') || errorMsg.includes('exists')) {
          toast.error("A user with this email already exists. Please use a different email.");
          setSaving(false);
          return;
        }

        toast.error("Failed to create profile. Please try again or contact support.");
        setSaving(false);
        return;
      }

      if (!result?.success) {
        console.error("matchmaker.create_profile_returned_error");
        const errorMessage = result?.error || "Failed to create profile";
        
        // Check for duplicate email errors
        if (errorMessage.toLowerCase().includes('already') || 
            errorMessage.toLowerCase().includes('registered') || 
            errorMessage.toLowerCase().includes('exists')) {
          toast.error("A user with this email already exists. Please use a different email.");
        } else {
          toast.error(errorMessage);
        }
        
        setSaving(false);
        return;
      }

      // Check if email failed but profile was created
      if (result.warning) {
        toast.warning(result.warning, { duration: 10000 });
        
        // Show the claim URL in an alert dialog
        const claimUrl = result.claim_url;
        setTimeout(() => {
          const shouldCopy = window.confirm(
            `Profile created successfully but email failed to send.\n\n` +
            `Please share this claim link with the user:\n\n` +
            `${claimUrl}\n\n` +
            `Click OK to copy the link to clipboard.`
          );
          
          if (shouldCopy) {
            navigator.clipboard.writeText(claimUrl);
            toast.success("Claim link copied to clipboard!");
          }
        }, 500);
      } else {
        toast.success("Profile created successfully! Invitation email sent.");
      }
      
      navigate("/matchmaker/profiles");
    } catch (error: any) {
      console.error("matchmaker.create_profile_failed");
      toast.error(error.message || "Failed to create profile");
    } finally {
      setSaving(false);
    }
  };

  const progressPercentage = ((currentStep + 1) / STEPS.length) * 100;

  return (
    <MatchmakerLayout title="Create New Profile">
      <div className="container mx-auto max-w-3xl px-4 py-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-primary/10">
              <UserPlus className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                Create Profile for User
              </h1>
              <p className="text-muted-foreground">
                Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep].title}
              </p>
            </div>
          </div>

          <div className="mt-4">
            <Progress value={progressPercentage} className="h-2" />
          </div>
        </div>

        {/* Important Notice */}
        <Alert className="mb-6 border-primary/20 bg-primary/5">
          <Info className="h-4 w-4" />
          <AlertDescription>
            You're creating a profile on behalf of a user. Ensure you have their explicit consent before proceeding.
            The user will receive an email invitation to claim and complete their profile.
          </AlertDescription>
        </Alert>

        {/* Quick Import - Only show on first step */}
        {currentStep === 0 && (
          <div className="mb-6">
            <QuickImport onExtract={handleQuickImport} />
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>{STEPS[currentStep].title}</CardTitle>
            <CardDescription>{STEPS[currentStep].description}</CardDescription>
          </CardHeader>
          <CardContent>
            {currentStep === 0 && (
              <Step1BasicInfo
                data={formData}
                onComplete={handleStepComplete}
                saving={saving}
              />
            )}

            {currentStep === 1 && (
              <Step2ReligiousProfile
                data={formData}
                onComplete={handleStepComplete}
                onBack={handleBack}
                saving={saving}
              />
            )}

            {currentStep === 2 && (
              <Step3EducationCareer
                data={formData}
                onComplete={handleStepComplete}
                onBack={handleBack}
                saving={saving}
              />
            )}

            {currentStep === 3 && (
              <Step4LifestylePersonality
                data={formData}
                onComplete={handleStepComplete}
                onBack={handleBack}
                saving={saving}
              />
            )}

            {currentStep === 4 && (
              <Step5Preferences
                data={formData}
                onComplete={handleStepComplete}
                onBack={handleBack}
                saving={saving}
              />
            )}

            {currentStep === 5 && (
              <div className="space-y-6">
                {/* Review Summary */}
                <div className="rounded-lg border p-4 space-y-2">
                  <h3 className="font-semibold">Profile Summary</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Name:</span>{" "}
                      <span className="font-medium">{formData.first_name || "Not provided"}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Age:</span>{" "}
                      <span className="font-medium">{formData.age}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Gender:</span>{" "}
                      <span className="font-medium capitalize">{formData.gender}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Location:</span>{" "}
                      <span className="font-medium">{formData.location || "Not provided"}</span>
                    </div>
                  </div>
                </div>

                {/* Invitation Email */}
                <div className="space-y-2">
                  <Label htmlFor="invite_email">
                    User's Email Address <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="invite_email"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="user@example.com"
                    required
                  />
                  <p className="text-sm text-muted-foreground">
                    An invitation will be sent to this email to claim the profile
                  </p>
                </div>

                {/* Contact details you already have for them. Optional — they can
                    fill these in themselves when they claim the profile. */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="contact_phone">Phone (optional)</Label>
                    <Input
                      id="contact_phone"
                      type="tel"
                      value={formData.phone || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          phone: formatPhoneInput(e.target.value),
                        }))
                      }
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_whatsapp">WhatsApp (optional)</Label>
                    <Input
                      id="contact_whatsapp"
                      type="tel"
                      value={formData.whatsapp || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          whatsapp: formatPhoneInput(e.target.value),
                        }))
                      }
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
                <p className="-mt-2 text-sm text-muted-foreground">
                  They'll choose how they prefer to be contacted when they claim
                  the profile.
                </p>

                {/* Consent Checkbox */}
                <div className="flex items-start space-x-3 rounded-lg border p-4 bg-muted/30">
                  <Checkbox
                    id="consent"
                    checked={consentGiven}
                    onCheckedChange={(checked) => setConsentGiven(checked as boolean)}
                  />
                  <div className="flex-1">
                    <Label
                      htmlFor="consent"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      I confirm that I have explicit consent from this person to create their profile
                      <span className="text-destructive ml-1">*</span>
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Creating profiles without consent violates our terms of service and may result in account suspension.
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBack}
                    disabled={saving}
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    onClick={handleFinalSubmit}
                    disabled={saving || !consentGiven || !inviteEmail}
                    className="flex-1"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating Profile...
                      </>
                    ) : (
                      "Create Profile & Send Invitation"
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MatchmakerLayout>
  );
}
